import { FC, useEffect, useMemo, useState } from 'react'

import { connect } from 'react-redux'
import styled from 'styled-components'

import {
  Box,
  Button,
  Dropdown,
  Flex,
  HorizontalLine,
} from 'app/components/design-system-components'
import { SearchRounded } from 'app/components/design-system-components/icons/actions'
import { ArrowDropDownRounded } from 'app/components/design-system-components/icons/navigation'
import { Input } from 'app/components/design-system-components/inputs'
import { CheckboxesList } from 'app/components/design-system-components/list-filters/CheckboxesList'
import { DropdownBodySectionHeader } from 'app/components/design-system-components/list-filters/DropdownBodySectionHeader'
import {
  fontSizes,
  fontWeight,
} from 'app/components/design-system-components/styles/variables'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { getAgencyMembers } from 'app/services/http/agency'
import * as snugNotifier from 'app/services/snugNotifier'
import { addPodsFormFields } from 'app/sm/team_viewing_preference/pods/AddPodModal'
import { MAX_POD_TEAM_MEMBERS } from 'app/sm/team_viewing_preference/pods/helper'
import { StyledDropdownButton } from 'app/sm/team_viewing_preference/states_dropdown'
import { useLoadingStates } from 'app/utils/hooks/useLoadingStates'

export const DropdownBodyWrapper = styled(Box)`
  box-shadow: 0 ${theme.space[1]}px ${theme.dividedSpace[6]}px 0
    ${theme.colors.dropdownDroppedShadow};
  background-color: ${theme.colors.white};

  .dropdown-header {
    font-weight: ${fontWeight[6]};
    font-size: ${theme.space[4]}px;
  }

  .dropdown-list {
    input[type='checkbox'] {
      margin-right: ${theme.space[1]}px;
    }

    label > span {
      font-size: ${fontSizes[4]}px;
    }
  }

  .dropdown-list:last-child {
    .form-checkbox {
      padding-bottom: ${theme.baseSpace}px;
    }
  }
`

export const StyledDropdownBodyWrapper = styled(DropdownBodyWrapper)`
  .dropdown-list:first-child {
    .form-checkbox {
      padding-top: ${theme.baseSpace}px;
    }
  }
`

const Wrapper = styled(Box)`
  margin-bottom: ${theme.space[4]}px;
`

const toDropdownType = (item: TeamItemType) => ({
  option: item?.guidID,
  optionMeta: {
    id: item?.guidID,
    displayText: item?.name,
    noSeparator: false,
  },
})

interface ISelectTeamMembersDropdownProps {
  teamID: any
  dropDownWidth: any
  handleApplyFilter: any
  formBags: any
  podTeamMembers: any
}

type TeamItemType = {
  name: string
  guidID: string
}

const SelectTeamMembersDropdown: FC<ISelectTeamMembersDropdownProps> = ({
  teamID,
  dropDownWidth,
  handleApplyFilter,
  formBags,
  podTeamMembers,
}) => {
  const [teams, setTeams] = useState<TeamItemType[]>([])

  const { values, setFieldValue } = formBags
  const selectedManagers = values[addPodsFormFields.managers]

  const {
    // loadingStates: teamMembersLoadingStates,
    loadingStatesHelpers: teamMembersLoadingStateHelpers,
  } = useLoadingStates()

  const [searchText, setSearchText] = useState('')

  const onSearchTextChange = ({ target: { value } }: any) => {
    setSearchText(value)
  }

  const options = useMemo(() => {
    return teams
      .map(toDropdownType)
      .filter(({ optionMeta: { displayText } }) =>
        displayText.toLowerCase().includes(searchText.toLowerCase()),
      )
      .sort(
        (a, b) =>
          (selectedManagers[b.option] ? 1 : 0) -
          (selectedManagers[a.option] ? 1 : 0),
      )
  }, [searchText, selectedManagers, teams])

  const onApply = (dropdownControls: any) => {
    handleApplyFilter(selectedManagers)
    setFieldValue(selectedManagers)
    dropdownControls.toggle()
  }

  const fetchTeamMembers = () => {
    teamMembersLoadingStateHelpers.startLoading()
    getAgencyMembers(teamID)
      .then((data: any) => {
        setTeams(
          data.map((item: any) => ({
            name: item.contact?.firstName + ' ' + item.contact?.lastName,
            guidID: item.contact?.guidID,
          })),
        )
        teamMembersLoadingStateHelpers.markDoneSuccessfully()
      })
      .catch((err: any) => {
        teamMembersLoadingStateHelpers.setError(err)
      })
  }

  useEffect(() => {
    fetchTeamMembers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDropdownButtonText = () => {
    let text = 'Click to select'

    if (Object.keys(selectedManagers).length > 0) {
      text = teams
        .filter((team) => Object.keys(selectedManagers).includes(team.guidID))
        .map((team) => team.name)
        .join(', ')
    }

    return text
  }

  const onCheckBoxClickedTeamOption = (checked: boolean, option: any) => {
    const teamMembersValues = values[addPodsFormFields.managers]

    if (checked) {
      if (Object.keys(teamMembersValues).length >= MAX_POD_TEAM_MEMBERS) {
        return snugNotifier.error(
          'Maximum number of team members reached for pod',
        )
      }

      setFieldValue(addPodsFormFields.managers, {
        ...teamMembersValues,
        [option.option]: true,
      })
    } else {
      const { [option.optionMeta.id]: uncheckedOption, ...otherOptions } =
        teamMembersValues
      setFieldValue(addPodsFormFields.managers, {
        ...otherOptions,
      })
    }
  }

  const onClickOutside = () => {
    setFieldValue(
      addPodsFormFields.managers,
      podTeamMembers.reduce((acc: { [key: string]: boolean }, item: string) => {
        acc[item] = true
        return acc
      }, {}),
    )
  }

  return (
    <Wrapper className="select_team_members_dropdown">
      <Flex alignItems="center">
        <Dropdown
          triggerWrapperComponent={({ dropdownControls }: any) => (
            <StyledDropdownButton
              variant="outline"
              onClick={dropdownControls.toggle}
              type="button"
              {...(dropDownWidth && { width: dropDownWidth })}
            >
              <Flex justifyContent="space-between">
                <Box className={'ellipsis'}>{getDropdownButtonText()}</Box>
                <Box
                  as={ArrowDropDownRounded}
                  ml={theme.space[3] + 'px'}
                  fontSize={theme.fontSizes.pExtraLarge18}
                />
              </Flex>
            </StyledDropdownButton>
          )}
          dropdownComponent={({ dropdownControls }: any) => (
            <StyledDropdownBodyWrapper>
              <Box p={theme.space[4] + 'px'}>
                <Input
                  icon={
                    <SearchRounded style={{ color: theme.colors.gray400 }} />
                  }
                  inputProps={{
                    onChange: onSearchTextChange,
                    placeholder: 'Search for a contact',
                  }}
                  id={undefined}
                  prefix={undefined}
                  postfix={undefined}
                  endedIcon={undefined}
                  containerProps={undefined}
                />
              </Box>

              <DropdownBodySectionHeader
                header={'ALL CONTACTS'}
                containerProps={{ mb: theme.space[4] + 'px' }}
              />

              <CheckboxesList
                options={options}
                checkedOptions={selectedManagers}
                onCheckBoxClicked={onCheckBoxClickedTeamOption}
                containerProps={{
                  className: 'dropdown-list',
                }}
              />

              <HorizontalLine mt="1px" />

              <Box boxShadow={theme.boxShadows.black} p={theme.space[3] + 'px'}>
                <Button
                  sizeVariant="regular"
                  width="100%"
                  onClick={onApply.bind(null, dropdownControls)}
                >
                  Apply
                </Button>
              </Box>
            </StyledDropdownBodyWrapper>
          )}
          topSpaceInPx={9}
          onClickOutsideCustom={onClickOutside}
        />
      </Flex>
    </Wrapper>
  )
}

const mapStateToProps = ({ session }: { session: any }) => ({
  currentTeam: session.currentTeam,
  currentAgency: session.currentAgency,
})

const mapDispatchToProps = () => ({})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SelectTeamMembersDropdown)
